// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The floor/storey picker: a compact collapsed-by-default pill stack over
 * the 3D/plan viewer, bottom-right (the "Google Maps indoor level picker"
 * layout) — `components/bygning/StoreyPicker.tsx` draws it, open/closed
 * interaction included (DOM-only, not covered here). It drives the same
 * `StoreyFilterState` Modelindstillinger › Geometri's Etage control used to
 * (frontend-r22.28 round 4 replaced that `<Select>` + mode toggle with this
 * picker) — not a separate focus — so picking a storey here sets the
 * clipping box in both the 3D view and the locked plan, exactly as the old
 * control did. Pure: no React, no DOM.
 */

import type { StoreyFilterState } from '../viewport/elementFilter';
import type { Storey } from './storeys';

/**
 * The floor-numbering convention a locale reads, frontend-4o5 (i18n): a
 * Danish/European building calls the entrance storey itself "S"/"G" and
 * starts counting above it at 1; a US building calls that same storey "1"
 * and starts counting above it at 2. Below ground, both count -1, -2, … —
 * no locale split is needed there (frontend-4o5 can still override it if a
 * future locale disagrees). `european` (the Nordic/German default) is what
 * every stored case today expects; `us` exists so a later locale picker has
 * something to switch to without changing this module.
 */
export type StoreyLabelConvention = 'european' | 'us';

/** `european`'s ground-storey glyph: Danish "S" (stueetage), else "G". */
export type StoreyLabelLanguage = 'da' | 'en';

export interface StoreyLabelOptions {
  /** @default 'european' */
  convention?: StoreyLabelConvention;
  /** Only read for `convention: 'european'`. @default 'da' */
  language?: StoreyLabelLanguage;
}

/** One row of the picker. */
export interface StoreyPickerItem {
  id: number;
  name: string;
  selected: boolean;
  /** The icon-like label ("S", "G", "1", "-1", …) — see `storeyShortLabel`. */
  shortLabel: string;
  /** The ground storey's own row (offset 0) — the collapsed button falls back to it when nothing is focused. */
  isGround: boolean;
}

/**
 * Two storeys or more only — one storey is no hierarchy to pick from, the
 * same threshold `deriveStoreys` itself applies (an empty `[]` there).
 */
export function showStoreyPicker(storeys: readonly Storey[]): boolean {
  return storeys.length > 1;
}

/**
 * The ground storey's position in `storeys` sorted lowest first — the one
 * whose floor (`zMin`, world z) sits closest to world z = 0, which is where
 * a scan's own SLAM origin (and so the project's world frame) sits in
 * practice: near the storey the scan started on, almost always the
 * building's entrance. The lowest storey is only a safe assumption for a
 * building with no basement — Sygehusgrunden has one, and picking index 0
 * there would label its basements "S"/"1. sal" instead of "-2"/"-1". Falls
 * back to the lowest storey only when nothing has a finite `zMin` (an empty
 * list, or every storey's height failed to resolve). `StoreyRecord` carries
 * a ground flag since schema v44 (`is_ground`, set by hand in Rediger's
 * Etage editor, never detected): a storey that carries it wins.
 */
export function groundStoreyIndex(storeysLowestFirst: readonly Storey[]): number {
  // Set by hand in the Etage editor (ReUseX schema v44 `is_ground`): it wins over the guess.
  const marked = storeysLowestFirst.findIndex((s) => s.isGround === true);
  if (marked >= 0) return marked;
  let ground = 0;
  let closest = Infinity;
  storeysLowestFirst.forEach((s, i) => {
    if (!Number.isFinite(s.zMin)) return;
    const distance = Math.abs(s.zMin);
    if (distance < closest) {
      closest = distance;
      ground = i;
    }
  });
  return ground;
}

/**
 * The compact label for a storey `offset` floors above (positive) or below
 * (negative) the ground storey — 0 is the ground storey itself. Derived
 * purely from position, never from the storey's own name: an imported
 * storey's name ("Kælder 1", "Stueplan", a bare Revit level name) may carry
 * no digit at all, or one that disagrees with its physical order.
 */
export function storeyShortLabel(offset: number, options?: StoreyLabelOptions): string {
  if (offset < 0) return String(offset); // -1, -2, … — both conventions agree below ground.
  const convention = options?.convention ?? 'european';
  if (convention === 'us') return String(offset + 1); // ground = "1", the next storey up = "2".
  if (offset > 0) return String(offset); // european: ground already took 0, so storey 1 above it = "1".
  return (options?.language ?? 'da') === 'en' ? 'G' : 'S';
}

/**
 * The picker's rows, highest storey first (`storeys` is lowest first, the
 * order rooms and `StoreyFilterState` use) — top of the stack = top of the
 * building, as a physical floor indicator reads. `selectedId`'s row is
 * flagged; no row is flagged for `null` ("Alle etager" — no clip).
 */
export function storeyPickerItems(storeys: readonly Storey[], selectedId: number | null, options?: StoreyLabelOptions): StoreyPickerItem[] {
  const lowestFirst = [...storeys].sort((a, b) => a.id - b.id);
  const ground = groundStoreyIndex(lowestFirst);
  return lowestFirst
    .map((s, i) => ({
      id: s.id,
      name: s.name,
      selected: s.id === selectedId,
      shortLabel: storeyShortLabel(i - ground, options),
      isGround: i === ground,
    }))
    .sort((a, b) => b.id - a.id);
}

/**
 * The filter after picking storey `id` from the picker: keeps the current
 * `andBelow` mode (sticky across a storey change — the old Etage `<Select>`
 * did the same, never resetting "… og under" back to "Kun …" just because
 * the storey changed).
 */
export function pickStoreyFilter(current: StoreyFilterState, id: number): StoreyFilterState {
  return { storeyId: id, andBelow: current.andBelow };
}

/**
 * The filter with its mode flipped — "Kun denne etage" ↔ "Denne etage og
 * under". A no-op with no storey picked ("Alle etager" has no mode to flip).
 */
export function toggleStoreyFilterMode(current: StoreyFilterState): StoreyFilterState {
  return current.storeyId === null ? current : { ...current, andBelow: !current.andBelow };
}

/** Back to "Alle etager" — no clip. */
export function clearStoreyFilter(): StoreyFilterState {
  return { storeyId: null, andBelow: false };
}
